import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { queryKeys } from "@/lib/query-keys";
import { receivableService } from "@/services/receivable";
import type { ReceivableListParams } from "@/services/receivable";

import type {
  CreateReceivablePaymentReq,
  UpdateReceivablePaymentReq,
  UpsertReceivableMetaReq,
} from "@artisancode/api-types";

export function useReceivables(params?: ReceivableListParams) {
  return useQuery({
    queryKey: queryKeys.receivables.list(params as Record<string, unknown>),
    queryFn: () => receivableService.list(params),
  });
}

function invalidateAfterMutation(qc: ReturnType<typeof useQueryClient>) {
  qc.invalidateQueries({ queryKey: queryKeys.receivables.all });
  qc.invalidateQueries({ queryKey: queryKeys.sales.all });
}

export function useRecordPayment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      saleId,
      idempotencyKey,
      ...body
    }: {
      saleId: string;
      idempotencyKey?: string;
    } & CreateReceivablePaymentReq) =>
      receivableService.recordPayment(saleId, body, idempotencyKey),
    onSuccess: () => invalidateAfterMutation(qc),
  });
}

export function useUpdatePayment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      paymentId,
      ...body
    }: { paymentId: string } & UpdateReceivablePaymentReq) =>
      receivableService.updatePayment(paymentId, body),
    onSuccess: () => invalidateAfterMutation(qc),
  });
}

export function useDeletePayment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (paymentId: string) =>
      receivableService.deletePayment(paymentId),
    onSuccess: () => invalidateAfterMutation(qc),
  });
}

export function useUpsertReceivableMeta() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      saleId,
      ...body
    }: { saleId: string } & UpsertReceivableMetaReq) =>
      receivableService.upsertMeta(saleId, body),
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: queryKeys.receivables.all }),
  });
}
